import React, { useEffect, useMemo, useReducer } from "react";
import { Switch, BrowserRouter, Route, Redirect } from "react-router-dom";
import { Grid, makeStyles } from "@material-ui/core";
import {
  Login,
  ResetPassword,
  UpdatePassword,
  DoctorsScreen,
  LaboratoriesScreen,
  ProductsApprovalScreen,
  AdminsScreen,
  OperatorsScreen,
  CategoriesScreen,
  ProductsScreen,
  AssignmentsScreen,
  BusinessUnitScreen,
  OrdersScreen,
  DoctorsApprovalScreen,
  ActiveDoctors,
  AssignToOperatorScreen,
  AssignedOperators,
  OperatorAssignments,
  DoctorAssignmentsTop,
  MostRequestedProducts,
  UsersInLaboratoryScreen,
  UsersInBusinessUnit,
  Reports,
  OperatorHistory,
  PatientsRepetitionRate,
  SamplesToRunOut,
  SamplesAvailableScreen,
} from "./screens";
import { userServices } from "./services";
import { AuthContext, SelectedLabProvider } from "./context";
import { ProtectedRoute, Layout } from "./components";
import AssignmentsToPatientsScreen from "./screens/assignments-to-patients";
import GetAvaibleProducts from "./screens/admin-products-screen";
import ZonesLayout from "./screens/laboratories/zones/layout";
import RefillLayout from "./screens/laboratories/refill/layout";
import AssignToRepresentativesScreen from "./screens/laboratories/representative/assign";
import ConsentLayout from "./screens/consent/layout";
import AssignToDoctorScreen from "./screens/assign-to-doctor";
import OperatorsWrongAddressScreen from "./screens/operators-screen-wrong-address";

const protectedRoutes = (roleName) => [
  {
    path: "/",
    component:
      roleName === "OPERADOR" || roleName === "CHILD_OPERATOR"
        ? OperatorAssignments
        : DoctorsScreen,
  },
  {
    path: "/doctors-approval",
    component: DoctorsApprovalScreen,
  },
  {
    path: "/active-doctors",
    component: ActiveDoctors,
  },
  {
    path: "/laboratories",
    component: LaboratoriesScreen,
  },
  {
    path: "/products-approval",
    component: ProductsApprovalScreen,
  },
  {
    path: "/users",
    component:
      roleName === "LABORATORY_ADMIN"
        ? UsersInLaboratoryScreen
        : roleName === "USER_LABORATORY"
          ? UsersInBusinessUnit
          : AdminsScreen,
  },
  {
    path: "/operators",
    component: OperatorsScreen,
  },
  {
    path: "/categories",
    component: CategoriesScreen,
  },
  {
    path: "/products",
    component: ProductsScreen,
  },
  {
    path: "/products-avaible",
    component: ProductsScreen,
  },
  {
    path: "/assign-to-representative",
    component: AssignToRepresentativesScreen,
  },
  // {
  //   path: "/assignments",
  //   component: AssignmentsScreen,
  // },
  {
    path: "/assign-to-doctors",
    component: AssignToDoctorScreen,
  },
  {
    path: "/assignments-to-patients",
    component: AssignmentsToPatientsScreen,
  },
  {
    path: "/assign-to-operator",
    component: AssignToOperatorScreen,
  },
  {
    path: "/history",
    component: OperatorHistory,
  },
  {
    path: "/assignments-wrong-address",
    component: OperatorsWrongAddressScreen,
  },
  {
    path: "/assigned-operators",
    component: AssignedOperators,
  },
  {
    path: "/orders",
    component: OrdersScreen,
  },
  {
    path: "/business-units",
    component: BusinessUnitScreen,
  },
  {
    path: "/doctor-assignments-top",
    component: DoctorAssignmentsTop,
  },
  {
    path: "/most-requested-products",
    component: MostRequestedProducts,
  },
  {
    path: "/reports",
    component: Reports,
  },
  {
    path: "/patients-repetition-rate",
    component: PatientsRepetitionRate,
  },
  {
    path: "/samples-to-run-out",
    component: SamplesToRunOut,
  },
  {
    path: "/samples-available",
    component: SamplesAvailableScreen,
  },
];

export default function Routes() {
  const classes = useStyles();

  const [authState, dispatch] = useReducer(
    (prevState, action) => {
      switch (action.type) {
        case "RESTORE_TOKEN":
          return {
            ...prevState,
            token: action.token,
            user: action.user,
          };
        case "LOGIN":
          return {
            ...prevState,
            token: action.token,
            user: action.user,
          };
        case "LOGOUT":
          return {
            ...prevState,
            token: null,
            user: null,
          };
        case "DRAWER":
          return {
            ...prevState,
            isOpen: action.isOpen,
          };
        default:
          return { ...prevState };
      }
    },
    {
      token: null,
      user: null,
      isOpen: false,
    }
  );

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const token = localStorage.getItem("token");
      dispatch({ type: "RESTORE_TOKEN", user: user, token: token });
    } catch (error) {
      console.log(error);
    }
  }, []);

  const provider = useMemo(
    () => ({
      signIn: ({ user, token, refreshToken }) => {
        try {
          localStorage.setItem("token", token);
          localStorage.setItem("refreshToken", refreshToken);
          localStorage.setItem("user", JSON.stringify(user));
        } catch (e) { }
        dispatch({ type: "LOGIN", user: user, token: token });
      },
      signOut: async () => {
        console.log("SignOut called - Debugging Logout");
        try {
          const refreshToken = localStorage.getItem("refreshToken");
          console.log("RefreshToken found inside signOut:", refreshToken);

          if (refreshToken) {
            console.log("Calling userServices.logout...");
            const result = await userServices.logout({ refreshToken });
            console.log("Logout API result:", result);
          } else {
            console.warn("No refreshToken found in localStorage during logout");
          }
        } catch (error) {
          console.error("Logout failed on server:", error);
        } finally {
          console.log("Clearing localStorage and dispatching LOGOUT");
          localStorage.clear();
          dispatch({ type: "LOGOUT" });
        }
      },
      setDrawer: (value) => {
        dispatch({ type: "DRAWER", isOpen: value });
      },
      authState,
    }),
    [authState]
  );

  return (
    <AuthContext.Provider value={provider}>
      <SelectedLabProvider>
      <BrowserRouter>
        <Grid container className={classes.routesContainer}>
          <Layout isOpen={authState.isOpen}>
            <Switch>
              {protectedRoutes(authState.user?.role.name).map(
                (option, index) => (
                  <ProtectedRoute
                    key={index}
                    exact
                    path={option.path}
                    token={authState.token}
                    auth={true}
                    component={() => <option.component />}
                  />
                )
              )}
              <ProtectedRoute
                path={"/zones"}
                token={authState.token}
                auth={true}
                component={ZonesLayout}
              />
              <ProtectedRoute
                path={"/refill"}
                token={authState.token}
                auth={true}
                component={RefillLayout}
              />
              <ProtectedRoute
                path={"/consent"}
                token={authState.token}
                auth={true}
                component={ConsentLayout}
              />
              {/* <ProtectedRoute
								path={'/representative'}
								token={authState.token}
								auth={true}
								component={RepresentativeLayout}
							/> */}
              <ProtectedRoute
                exact
                path="/login"
                auth={false}
                token={authState.token}
                component={Login}
              />
              <Route
                exact
                path="/account/reset-password"
                component={ResetPassword}
              />
              <Route exact path="/update-password" component={UpdatePassword} />
              <Route component={() => <Redirect to="/" />}></Route>
            </Switch>
          </Layout>
        </Grid>
      </BrowserRouter>
      </SelectedLabProvider>
    </AuthContext.Provider>
  );
}

const useStyles = makeStyles((theme) => ({
  routesContainer: {
    "& > div:nth-child(1)": {
      flex: 1,
    },
  },
}));
